import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  LedgerSettings,
  JournalSettings,
  VoucherSettings,
  COAMapping,
  mockLedgerSettings,
  mockJournalSettings,
  mockVoucherSettings,
  mockCOAMappings,
} from "../mock.data";

interface ConfigurationContextType {
  // Settings
  ledgerSettings: LedgerSettings;
  journalSettings: JournalSettings;
  voucherSettings: VoucherSettings;
  coaMappings: COAMapping[];

  // Update functions
  updateLedgerSettings: (updates: Partial<LedgerSettings>) => void;
  updateJournalSettings: (updates: Partial<JournalSettings>) => void;
  updateVoucherSettings: (updates: Partial<VoucherSettings>) => void;
  updateCOAMappings: (mappings: COAMapping[]) => void;
}

const ConfigurationContext = createContext<
  ConfigurationContextType | undefined
>(undefined);

interface ConfigurationProviderProps {
  children: ReactNode;
}

export const ConfigurationProvider: React.FC<ConfigurationProviderProps> = ({
  children,
}) => {
  const [ledgerSettings, setLedgerSettings] =
    useState<LedgerSettings>(mockLedgerSettings);
  const [journalSettings, setJournalSettings] =
    useState<JournalSettings>(mockJournalSettings);
  const [voucherSettings, setVoucherSettings] =
    useState<VoucherSettings>(mockVoucherSettings);
  const [coaMappings, setCOAMappings] = useState<COAMapping[]>(mockCOAMappings);

  const updateLedgerSettings = (updates: Partial<LedgerSettings>) => {
    setLedgerSettings((prev) => ({
      ...prev,
      ...updates,
      updated_at: Date.now(),
    }));
  };

  const updateJournalSettings = (updates: Partial<JournalSettings>) => {
    setJournalSettings((prev) => ({
      ...prev,
      ...updates,
      updated_at: Date.now(),
    }));
  };

  const updateVoucherSettings = (updates: Partial<VoucherSettings>) => {
    setVoucherSettings((prev) => ({
      ...prev,
      ...updates,
      updated_at: Date.now(),
    }));
  };

  const updateCOAMappings = (mappings: COAMapping[]) => {
    setCOAMappings(mappings);
  };

  const value: ConfigurationContextType = {
    ledgerSettings,
    journalSettings,
    voucherSettings,
    coaMappings,
    updateLedgerSettings,
    updateJournalSettings,
    updateVoucherSettings,
    updateCOAMappings,
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};

export const useConfiguration = (): ConfigurationContextType => {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error(
      "useConfiguration must be used within a ConfigurationProvider"
    );
  }
  return context;
};
